/**
 * TutorialContext
 *
 * Provides tutorial state management and actions throughout the application.
 * Handles tutorial progress, navigation, and persistence.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as tutorialService from '../services/tutorialService';
import {
  TUTORIAL_TRACKS,
  TUTORIAL_STEPS,
  TUTORIAL_VERSIONS,
  STEP_LIFECYCLE,
  TUTORIAL_PACKAGE_REQUIREMENTS,
  TUTORIAL_COMPONENT_REQUIREMENTS,
  getTutorialSteps,
  getTutorialVersion,
} from '../constants/tutorialConstants';

// =============================================================================
// CONTEXT
// =============================================================================

const TutorialContext = createContext(null);

// =============================================================================
// PROVIDER
// =============================================================================

export function TutorialProvider({ children, autoStart = true }) {
  const { user, actingOrganization } = useAuth();

  // Tutorial state
  const [state, setState] = useState({
    activeTrack: null,
    currentStepIndex: 0,
    isActive: false,
    completedTracks: [],
    completedSteps: {},
    completedVersions: {},
    dismissedThisSession: false,
    lastActiveTrack: null,
    lastActiveStepId: null,
    isLoading: true,
    isSaving: false,
  });

  // Entitlements state
  const [entitlements, setEntitlements] = useState(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);

  // Debounce ref for saving
  const saveTimeoutRef = useRef(null);

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  // Load entitlements
  useEffect(() => {
    const loadEntitlements = async () => {
      if (!user) {
        setEntitlements(null);
        setEntitlementsLoading(false);
        return;
      }

      setEntitlementsLoading(true);
      try {
        const data = await tutorialService.getEntitlements();
        setEntitlements(data);
      } catch (error) {
        console.error('Failed to load entitlements:', error);
      } finally {
        setEntitlementsLoading(false);
      }
    };

    loadEntitlements();
  }, [user, actingOrganization?.id]);

  // Load tutorial state from preferences
  useEffect(() => {
    const loadTutorialState = async () => {
      if (!user) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const tutorialState = user.ui_preferences?.tutorial_state || {};

        setState(prev => ({
          ...prev,
          completedTracks: tutorialState.completed_tracks || [],
          completedSteps: tutorialState.completed_steps || {},
          completedVersions: tutorialState.completed_versions || {},
          lastActiveTrack: tutorialState.last_active_track || null,
          lastActiveStepId: tutorialState.last_active_step_id || null,
          isLoading: false,
        }));
      } catch (error) {
        console.error('Failed to load tutorial state:', error);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    loadTutorialState();
  }, [user]);

  // =============================================================================
  // STEP FILTERING
  // =============================================================================

  /**
   * Filter steps based on lifecycle, entitlements, and user role
   */
  const filterSteps = useCallback((steps) => {
    if (!steps) return [];

    return steps.filter(step => {
      // 1. Check lifecycle state
      if (step.lifecycle?.state === STEP_LIFECYCLE.REMOVED) return false;
      if (step.lifecycle?.state === STEP_LIFECYCLE.PREVIEW) {
        // Only show preview steps if explicitly enabled
        return false;
      }

      // 2. Check package feature requirements
      const packageReq = TUTORIAL_PACKAGE_REQUIREMENTS[step.id];
      if (packageReq && entitlements) {
        const hasRequiredFeatures = packageReq.packageFeatures.every(
          feature => entitlements.features?.[feature] === true
        );
        if (!hasRequiredFeatures) return false;
      }

      // 3. Check component requirements
      const componentReq = TUTORIAL_COMPONENT_REQUIREMENTS[step.id];
      if (componentReq && entitlements) {
        const components = entitlements.components || [];
        // Empty components array means all are available
        if (components.length > 0 && !components.includes(componentReq.componentType)) {
          return false;
        }
      }

      return true;
    });
  }, [entitlements]);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  /**
   * Get filtered steps for the active track
   */
  const filteredSteps = useMemo(() => {
    if (!state.activeTrack) return [];
    const allSteps = getTutorialSteps(state.activeTrack);
    return filterSteps(allSteps);
  }, [state.activeTrack, filterSteps]);

  /**
   * Get current step data
   */
  const currentStepData = useMemo(() => {
    if (!state.isActive || !filteredSteps.length) return null;
    return filteredSteps[state.currentStepIndex] || null;
  }, [state.isActive, state.currentStepIndex, filteredSteps]);

  /**
   * Calculate progress
   */
  const progress = useMemo(() => {
    const total = filteredSteps.length;
    const current = state.currentStepIndex + 1;
    const percentage = total > 0 ? (current / total) * 100 : 0;
    return { current, total, percentage };
  }, [state.currentStepIndex, filteredSteps.length]);

  /**
   * Determine the appropriate tutorial track for the user
   */
  const determineTrack = useCallback(() => {
    if (!user) return TUTORIAL_TRACKS.EA;

    // Speaker portal accounts get the short, view-only speaker tour
    if (user.role === 'speaker') return TUTORIAL_TRACKS.SPEAKER;

    // Speaker bureau orgs get the paradigm-specific planner walkthrough
    // instead of the executive-travel-worded EA track. Admins of these orgs
    // are onboarded by the standalone Getting Started guide (/getting-started),
    // so their walkthrough track is the day-to-day planner tour too.
    const isSpeakerBureau = entitlements?.paradigm === 'speaker_bureau';

    const isOrgAdmin =
      user.role === 'superadmin' ||
      user.is_organization_admin === true;

    if (isSpeakerBureau) return TUTORIAL_TRACKS.MEETING_PLANNER;

    return isOrgAdmin ? TUTORIAL_TRACKS.ORG_ADMIN : TUTORIAL_TRACKS.EA;
  }, [user, entitlements]);

  /**
   * Check if user can resume a tutorial
   */
  const canResume = useMemo(() => {
    return (
      state.lastActiveTrack &&
      state.lastActiveStepId &&
      !state.completedTracks.includes(state.lastActiveTrack)
    );
  }, [state.lastActiveTrack, state.lastActiveStepId, state.completedTracks]);

  /**
   * Get available tracks for the user
   */
  const getAvailableTracks = useCallback(() => {
    const userTrack = determineTrack();

    // Superadmins can run every track, so they can review any tutorial
    // regardless of which organization they're acting as
    if (user?.role === 'superadmin') {
      const allTracks = Object.values(TUTORIAL_TRACKS);
      return [userTrack, ...allTracks.filter(t => t !== userTrack)];
    }

    const tracks = [userTrack];

    // Org admins can also access their org's day-to-day track
    if (user?.is_organization_admin && userTrack === TUTORIAL_TRACKS.ORG_ADMIN) {
      tracks.push(TUTORIAL_TRACKS.EA);
    }

    return tracks;
  }, [user, determineTrack]);

  // =============================================================================
  // PERSISTENCE
  // =============================================================================

  /**
   * Save state to backend (debounced)
   */
  const saveState = useCallback(async (stateToSave) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      setState(prev => ({ ...prev, isSaving: true }));

      try {
        await tutorialService.updateTutorialState({
          completed_tracks: stateToSave.completedTracks,
          completed_steps: stateToSave.completedSteps,
          completed_versions: stateToSave.completedVersions,
          last_active_track: stateToSave.activeTrack,
          last_active_step_id: stateToSave.currentStepId,
        });
      } catch (error) {
        console.error('Failed to save tutorial state:', error);
      } finally {
        setState(prev => ({ ...prev, isSaving: false }));
      }
    }, 1000); // 1 second debounce
  }, []);

  // =============================================================================
  // ACTIONS
  // =============================================================================

  /**
   * Start a tutorial track
   */
  const startTutorial = useCallback((track = null) => {
    const selectedTrack = track || determineTrack();
    const steps = getTutorialSteps(selectedTrack);
    const filtered = filterSteps(steps);

    if (filtered.length === 0) {
      console.warn('No steps available for track:', selectedTrack);
      return;
    }

    // Find resume position if available
    let startIndex = 0;
    if (state.lastActiveTrack === selectedTrack && state.lastActiveStepId) {
      const resumeIndex = filtered.findIndex(s => s.id === state.lastActiveStepId);
      if (resumeIndex >= 0) {
        startIndex = resumeIndex;
      }
    }

    setState(prev => ({
      ...prev,
      activeTrack: selectedTrack,
      currentStepIndex: startIndex,
      isActive: true,
      dismissedThisSession: false,
    }));

    // Save position
    saveState({
      ...state,
      activeTrack: selectedTrack,
      currentStepId: filtered[startIndex]?.id,
      completedTracks: state.completedTracks,
      completedSteps: state.completedSteps,
      completedVersions: state.completedVersions,
    });
  }, [determineTrack, filterSteps, state, saveState]);

  /**
   * Go to next step
   */
  const nextStep = useCallback(() => {
    if (!state.isActive) return;

    const nextIndex = state.currentStepIndex + 1;

    if (nextIndex >= filteredSteps.length) {
      // Complete the tutorial
      completeTutorial();
      return;
    }

    const nextStepData = filteredSteps[nextIndex];

    setState(prev => ({
      ...prev,
      currentStepIndex: nextIndex,
    }));

    // Mark previous step as completed and save
    const currentStep = filteredSteps[state.currentStepIndex];
    if (currentStep) {
      const newCompletedSteps = { ...state.completedSteps };
      if (!newCompletedSteps[state.activeTrack]) {
        newCompletedSteps[state.activeTrack] = [];
      }
      if (!newCompletedSteps[state.activeTrack].includes(currentStep.id)) {
        newCompletedSteps[state.activeTrack].push(currentStep.id);
      }

      saveState({
        ...state,
        activeTrack: state.activeTrack,
        currentStepId: nextStepData?.id,
        completedTracks: state.completedTracks,
        completedSteps: newCompletedSteps,
        completedVersions: state.completedVersions,
      });
    }
  }, [state, filteredSteps, saveState]);

  /**
   * Go to previous step
   */
  const previousStep = useCallback(() => {
    if (!state.isActive || state.currentStepIndex === 0) return;

    const prevIndex = state.currentStepIndex - 1;
    const prevStepData = filteredSteps[prevIndex];

    setState(prev => ({
      ...prev,
      currentStepIndex: prevIndex,
    }));

    saveState({
      ...state,
      activeTrack: state.activeTrack,
      currentStepId: prevStepData?.id,
      completedTracks: state.completedTracks,
      completedSteps: state.completedSteps,
      completedVersions: state.completedVersions,
    });
  }, [state, filteredSteps, saveState]);

  /**
   * Skip current step
   */
  const skipStep = useCallback(() => {
    nextStep();
  }, [nextStep]);

  /**
   * Go to a specific step by index
   */
  const goToStep = useCallback((stepIndex) => {
    if (!state.isActive || stepIndex < 0 || stepIndex >= filteredSteps.length) return;

    const stepData = filteredSteps[stepIndex];

    setState(prev => ({
      ...prev,
      currentStepIndex: stepIndex,
    }));

    saveState({
      ...state,
      activeTrack: state.activeTrack,
      currentStepId: stepData?.id,
      completedTracks: state.completedTracks,
      completedSteps: state.completedSteps,
      completedVersions: state.completedVersions,
    });
  }, [state, filteredSteps, saveState]);

  /**
   * Complete the tutorial
   */
  const completeTutorial = useCallback(async () => {
    if (!state.activeTrack) return;

    const newCompletedTracks = [...state.completedTracks];
    if (!newCompletedTracks.includes(state.activeTrack)) {
      newCompletedTracks.push(state.activeTrack);
    }

    const newCompletedVersions = { ...state.completedVersions };
    newCompletedVersions[state.activeTrack] = getTutorialVersion(state.activeTrack);

    setState(prev => ({
      ...prev,
      isActive: false,
      activeTrack: null,
      currentStepIndex: 0,
      completedTracks: newCompletedTracks,
      completedVersions: newCompletedVersions,
      lastActiveTrack: null,
      lastActiveStepId: null,
    }));

    try {
      await tutorialService.completeTutorialTrack(
        state.activeTrack,
        getTutorialVersion(state.activeTrack)
      );
    } catch (error) {
      console.error('Failed to save tutorial completion:', error);
    }
  }, [state]);

  /**
   * Dismiss the tutorial
   */
  const dismissTutorial = useCallback(async () => {
    setState(prev => ({
      ...prev,
      isActive: false,
      dismissedThisSession: true,
    }));

    try {
      await tutorialService.dismissTutorial();
    } catch (error) {
      console.error('Failed to dismiss tutorial:', error);
    }
  }, []);

  /**
   * Reset tutorial for a track or all tracks
   */
  const resetTutorial = useCallback(async (track = null) => {
    try {
      await tutorialService.resetTutorial(track);

      if (track) {
        setState(prev => ({
          ...prev,
          completedTracks: prev.completedTracks.filter(t => t !== track),
          completedSteps: {
            ...prev.completedSteps,
            [track]: [],
          },
          completedVersions: {
            ...prev.completedVersions,
            [track]: undefined,
          },
        }));
      } else {
        setState(prev => ({
          ...prev,
          completedTracks: [],
          completedSteps: {},
          completedVersions: {},
          lastActiveTrack: null,
          lastActiveStepId: null,
        }));
      }
    } catch (error) {
      console.error('Failed to reset tutorial:', error);
    }
  }, []);

  /**
   * Check if tutorial should show (for auto-start logic)
   */
  /**
   * Whether tutorials are enabled for this organization
   */
  const tutorialEnabledForOrg = useMemo(() => {
    if (!entitlements) return true; // Default to enabled while loading
    return entitlements.tutorial_enabled !== false;
  }, [entitlements]);

  const shouldShowTutorial = useCallback(() => {
    if (!user) return false;
    if (state.dismissedThisSession) return false;
    if (state.isLoading || entitlementsLoading) return false;

    // Check org-level toggle
    if (!tutorialEnabledForOrg) return false;

    // Speaker bureau org admins are onboarded via the standalone Getting
    // Started guide (auto-opened once from the dashboard), not a walkthrough
    if (
      entitlements?.paradigm === 'speaker_bureau' &&
      (user.role === 'superadmin' || user.is_organization_admin === true)
    ) {
      return false;
    }

    const userTrack = determineTrack();

    // Check if user has completed this track
    if (state.completedTracks.includes(userTrack)) return false;

    // Check if auto-start is disabled
    const tutorialState = user.ui_preferences?.tutorial_state;
    if (tutorialState?.auto_start_disabled) return false;

    return autoStart;
  }, [user, state, entitlements, entitlementsLoading, tutorialEnabledForOrg, determineTrack, autoStart]);

  // =============================================================================
  // SELF-HEAL
  // =============================================================================

  // If the active step index ever runs past the end of the filtered steps
  // (e.g. steps got filtered out mid-tour), finish the tutorial cleanly
  // instead of leaving an invisible-but-active overlay
  useEffect(() => {
    if (!state.isActive || state.isLoading || entitlementsLoading) return;
    if (filteredSteps.length > 0 && state.currentStepIndex >= filteredSteps.length) {
      console.warn('Tutorial step index out of range; completing tutorial', {
        track: state.activeTrack,
        index: state.currentStepIndex,
        steps: filteredSteps.length,
      });
      completeTutorial();
    }
  }, [state.isActive, state.isLoading, entitlementsLoading, state.currentStepIndex, state.activeTrack, filteredSteps.length, completeTutorial]);

  // =============================================================================
  // CONTEXT VALUE
  // =============================================================================

  const value = useMemo(() => ({
    // State
    state,
    entitlements,
    isLoading: state.isLoading || entitlementsLoading,

    // Current step
    currentStepData,
    filteredSteps,

    // Progress
    progress,

    // Flags
    canResume,
    tutorialEnabledForOrg,

    // Actions
    startTutorial,
    nextStep,
    previousStep,
    skipStep,
    goToStep,
    completeTutorial,
    dismissTutorial,
    resetTutorial,

    // Helpers
    shouldShowTutorial,
    determineTrack,
    getAvailableTracks,
  }), [
    state,
    entitlements,
    entitlementsLoading,
    currentStepData,
    filteredSteps,
    progress,
    canResume,
    tutorialEnabledForOrg,
    startTutorial,
    nextStep,
    previousStep,
    skipStep,
    goToStep,
    completeTutorial,
    dismissTutorial,
    resetTutorial,
    shouldShowTutorial,
    determineTrack,
    getAvailableTracks,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

export default TutorialContext;
